import { Board, Note } from '../types';

interface SearchResult {
    boardId: string;
    boardTitle: string;
    noteId: string;
    noteTitle: string;
    noteType: string;
    matchField: 'title' | 'content';
    snippet: string;
}

// Simple in-memory search without FlexSearch for compatibility
class SearchService {
    private boards: Map<string, Board> = new Map();

    indexBoard(board: Board): void {
        this.boards.set(board.id, board);
    }

    removeBoard(boardId: string): void {
        this.boards.delete(boardId);
    }

    indexAllBoards(boards: Board[]): void {
        this.boards.clear();
        boards.forEach(board => {
            if (!board.isDeleted) {
                this.boards.set(board.id, board);
            }
        });
    }

    search(query: string, limit = 20): SearchResult[] {
        if (!query.trim()) return [];

        const lowerQuery = query.toLowerCase();
        const results: SearchResult[] = [];

        this.boards.forEach((board, boardId) => {
            board.notes.forEach(note => {
                // Search in title
                if (note.title && note.title.toLowerCase().includes(lowerQuery)) {
                    results.push({
                        boardId,
                        boardTitle: board.title,
                        noteId: note.id,
                        noteTitle: note.title || 'Untitled',
                        noteType: note.type.toLowerCase(),
                        matchField: 'title',
                        snippet: this.createSnippet(note.title, query),
                    });
                    return; // Don't add duplicate for same note
                }

                // Search in content
                const content = this.getNoteContent(note);
                if (content.toLowerCase().includes(lowerQuery)) {
                    results.push({
                        boardId,
                        boardTitle: board.title,
                        noteId: note.id,
                        noteTitle: note.title || 'Untitled',
                        noteType: note.type.toLowerCase(),
                        matchField: 'content',
                        snippet: this.createSnippet(content, query),
                    });
                }
            });
        });

        return results.slice(0, limit);
    }

    private getNoteContent(note: Note): string {
        let content = note.content || '';

        // Include list items
        if (note.items) {
            content += ' ' + note.items.map(item => item.text).join(' ');
        }

        // Include planner items
        if (note.plannerItems) {
            content += ' ' + note.plannerItems.map(item => item.title).join(' ');
        }

        return content;
    }

    private createSnippet(text: string, query: string): string {
        const lowerText = text.toLowerCase();
        const lowerQuery = query.toLowerCase();
        const index = lowerText.indexOf(lowerQuery);

        if (index === -1) {
            return text.slice(0, 100) + (text.length > 100 ? '...' : '');
        }

        const start = Math.max(0, index - 30);
        const end = Math.min(text.length, index + query.length + 50);
        let snippet = text.slice(start, end);

        if (start > 0) snippet = '...' + snippet;
        if (end < text.length) snippet += '...';

        return snippet;
    }
}

// Singleton instance
export const searchService = new SearchService();
export type { SearchResult };
